import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { firstValueFrom, of } from "rxjs";
import { AppRoutes } from "src/app/app-routing.module";
import { EaredDoveService } from "../../eared-dove/eared-dove.service";
import { PadlockService } from "../../padlock/padlock.service";
import {
  GetPadlockTokenSuccess,
  PadlockTokenActions,
  PadlockTokenActionTypes,
} from "../../padlock/state/padlock-token.actions";
import { WorkspaceRoutes } from "../workspace.routes";
import {
  CreateWorkspaceFailure,
  DeleteWorkspaceFailure,
  LoadWorkspaces,
  LoadWorkspacesFailure,
  SetActiveWorkspace,
  SetWorkspaces,
  WorkspaceActionTypes,
  WorkspacesActions,
} from "./workspaces.actions";

@Injectable()
export class WorkspacesEffects {
  padlockTokenSet = createEffect(() =>
    this.actions$.pipe(
      ofType(PadlockTokenActionTypes.GetPadlockTokenSuccess),
      filter((action) => !!action.token),
      tap(() => {
        this.logger.debug(`SIDE EFFECT: Fetching workspaces for user`);
        this.store.dispatch(new LoadWorkspaces());
      }),
      switchMap((action) => {
        // Platform admins see every workspace, so use the details endpoint that
        // is not scoped to workspaces the user directly belongs to.
        const isPlatformAdmin =
          this.padlock.getDecodedToken(action.token)?.platform_admin === true;
        const workspaces$ = isPlatformAdmin
          ? this.earedDove.getWorkspaceDetails()
          : this.earedDove.getBoSpecificWorkspaces();

        return workspaces$.pipe(
          map((workspaces) => {
            this.logger.debug(`SIDE EFFECT: Workspaces fetched successfully`);
            return new SetWorkspaces(workspaces);
          }),
          catchError((error) => {
            this.logger.error(`SIDE EFFECT: Error fetching workspaces`, error);
            return of(new LoadWorkspacesFailure(error));
          }),
        );
      }),
    ),
  );

  createWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActionTypes.CreateWorkspace),
      concatMap(async (action) => {
        this.logger.debug(
          `SIDE EFFECT: ${WorkspaceActionTypes.CreateWorkspace}`,
        );

        // Creating workspaces is complicated:
        try {
          let workspace: any = action.workspace;

          // Create the tenant in Padlock
          this.logger.trace(`Create tenant in Padlock`);
          const padlockResponse: any = await firstValueFrom(
            this.padlock.createTenant(workspace),
          );

          // Obtain the new tenant w/ its uuid and name as created by padlock
          workspace = padlockResponse.tenant;

          // For some reason, this route calls "owner" "owner_uuid"
          workspace.owner = padlockResponse.tenant.owner_uuid;
          delete workspace.owner_uuid;

          // Obtain new JWT w/ new tenant & permissions from response and update store
          const jwt = padlockResponse.jwt.token;
          this.logger.trace(`Update the JWT`);
          this.store.dispatch(new GetPadlockTokenSuccess(jwt));

          // Create the corresponding workspace in Eared Dove
          this.logger.trace(`Create workspace in Eared Dove`);
          await firstValueFrom(this.earedDove.createWorkspace(workspace));

          // We need to do this again to get a new list of workspaces
          this.store.dispatch(new GetPadlockTokenSuccess(jwt));

          // Dispatch a new action
          this.logger.trace(`Workspace created successfully!`);
          return new SetActiveWorkspace(workspace, true);
        } catch (e) {
          this.logger.error(`Workspace not created`, e);
          return new CreateWorkspaceFailure(e);
        }
      }),
    ),
  );

  setActiveWorkspace$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkspaceActionTypes.SetActiveWorkspace),
        tap((action) => {
          if (action.workspace) {
            this.logger.debug(`ACTIVATE WORKSPACE`, action.workspace.label);

            // Navigate if requested
            if (action.navigate) {
              this.router.navigate([
                AppRoutes.WORKSPACE,
                action.workspace.name,
                WorkspaceRoutes.SURVEY_ROOT,
              ]);
            }
          } else {
            this.logger.warn(`NO WORKSPACE TO ACTIVATE`);
            if (action.navigate) {
              this.router.navigate([AppRoutes.ROOT]);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  deleteWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActionTypes.DeleteWorkspace),
      concatMap(async (action) => {
        try {
          // Delete in eared dove
          this.logger.trace(`Delete workspace from eared dove`);
          await firstValueFrom(
            this.earedDove.deleteWorkspace(action.workspace),
          );

          // Delete in padlock
          this.logger.trace(`Delete workspace from padlock`);
          const padlockResponse: any = await firstValueFrom(
            this.padlock.deleteTenant(action.workspace),
          );

          // Obtain new JWT w/ new tenant & permissions from response and update store
          const jwt = padlockResponse.token;
          this.logger.trace(`Update the JWT`);
          this.store.dispatch(new GetPadlockTokenSuccess(jwt));

          return new SetActiveWorkspace(null, action.navigate);
        } catch (e) {
          this.logger.error(`Workspace not deleted`, e);
          return new DeleteWorkspaceFailure(e);
        }
      }),
    ),
  );

  constructor(
    private actions$: Actions<WorkspacesActions | PadlockTokenActions>,
    private logger: NGXLogger,
    private padlock: PadlockService,
    private store: Store<any>,
    private router: Router,
    private earedDove: EaredDoveService,
  ) {}
}
